// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — champ « Lien vers un autre outil »
// ═══════════════════════════════════════════════════════════════════════════
//
// Câblage des deux pictos posés à côté du champ : ouvrir dans un nouvel onglet
// et copier dans le presse-papier. Extrait dans son propre module pour ne pas
// alourdir `renderActionEditor` (cliquet taille-fonction).
//
// Sécurité : on n'ouvre JAMAIS la valeur brute du champ. `safeExternalUrl`
// (même règle que le serveur) refuse tout ce qui n'est pas http(s) — sans quoi
// un `javascript:…` déjà en base s'exécuterait dans la session de celui qui
// clique. Les deux boutons sont désactivés tant que la saisie n'est pas une
// URL valide, ce qui rend la règle visible avant l'enregistrement.
//
// Bare refs utilisées (résolues via core-globals.d.ts) : t, toast, safeWarn.

import { safeExternalUrl } from '../../domain/external-url.js';

const INPUT_ID = 'ea-external-link';
const OPEN_ID = 'ea-external-link-open';
const COPY_ID = 'ea-external-link-copy';

function _el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

/** Active/désactive les deux pictos selon la validité de la saisie. */
function _refreshButtons(): void {
  const input = _el<HTMLInputElement>(INPUT_ID);
  if (!input) return;
  const raw = input.value.trim();
  const url = safeExternalUrl(raw);
  const enabled = url !== null;
  for (const id of [OPEN_ID, COPY_ID]) {
    const btn = _el<HTMLButtonElement>(id);
    if (!btn) continue;
    btn.disabled = !enabled;
    btn.style.opacity = enabled ? '' : '.45';
    btn.style.cursor = enabled ? '' : 'not-allowed';
  }
  // Signale une saisie non vide mais invalide, sans bloquer l'enregistrement
  // (le serveur refusera, avec le même message).
  const invalid = raw !== '' && !enabled;
  input.style.borderColor = invalid ? 'var(--danger, #b91c1c)' : '';
  input.title = invalid ? t('actionModal.externalLink.invalid') : '';
}

async function _copy(url: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast(t('actionModal.externalLink.copied'), 'success');
  } catch (e: unknown) {
    // Presse-papier refusé (contexte non sécurisé, permission) : repli sur la
    // sélection + `execCommand`, comme le bouton « Copier » de l'invitation.
    // On copie l'URL NORMALISÉE, pas la valeur brute du champ : sans quoi le
    // repli livrerait « jira.example.com/X » là où le clic principal donne
    // « https://jira.example.com/X », en annonçant la même chose.
    safeWarn('clipboard.writeText (lien action)', e);
    const input = _el<HTMLInputElement>(INPUT_ID);
    if (!input) return;
    const previous = input.value;
    input.value = url;
    input.select();
    const copied = document.execCommand('copy');
    input.value = previous;
    input.setSelectionRange(previous.length, previous.length);
    if (copied) toast(t('actionModal.externalLink.copied'), 'success');
  }
}

/**
 * Câble le champ « Lien vers un autre outil ». Sans effet si le champ n'est
 * pas dans le DOM (éditeur rendu partiellement, tests unitaires).
 *
 * `onChange` est la réaction générique de l'éditeur à une saisie (bascule des
 * CTA + auto-note) : elle est branchée ici, sur le même événement `input` que
 * le rafraîchissement des pictos, plutôt qu'une deuxième fois côté appelant.
 */
export function wireExternalLinkField(onChange: () => void): void {
  const input = _el<HTMLInputElement>(INPUT_ID);
  if (!input) return;
  _refreshButtons();
  input.addEventListener('input', () => {
    _refreshButtons();
    onChange();
  });
  _el(OPEN_ID)?.addEventListener('click', () => {
    const url = safeExternalUrl(input.value);
    if (!url) return;
    // `noopener` : la page ouverte ne doit pas garder la main sur la nôtre
    // via `window.opener`.
    window.open(url, '_blank', 'noopener,noreferrer');
  });
  _el(COPY_ID)?.addEventListener('click', () => {
    const url = safeExternalUrl(input.value);
    if (!url) return;
    void _copy(url);
  });
}
